import { Box, Flex, Icon } from '@chakra-ui/react';
import React, { useEffect, useRef } from 'react';
import { SchDurationTimeFieldsHelpers } from './SchDurationTimeFields.helpers';
import moment from 'moment-timezone';
import { KText } from '@/ui-library/components';
import { Edit05 } from '@kula-ai/icons/dual-tone/Edit05';
import { components } from 'react-select';
import { PiCalendarBlankDuotone } from 'react-icons/pi';
import { Clock } from '@kula-ai/icons/dual-tone/Clock';
import { useDateFormat } from '@/hooks';
import { SchBulkConflictError } from './SchBulkConflictError';
import * as Yup from 'yup';
import { bulkManualSchedulerUtils } from '@/pages/KInterviewScheduler/BulkManualScheduler/bulkManualScheduler.utils';

const inputStyles = {
  border: '2px solid !important',
  borderColor: 'var(--kula-colors-border-color) !important',
  height: '48px',
  fontSize: '0.875rem',
  outlineOffset: 0,
  _hover: {
    outline: '4px solid #6220A30F !important',
    border: '2px solid #605AFB !important',
    boxShadow: 'none !important',
  },
  _focus: {
    outline: '4px solid #6220A30F !important',
    border: '2px solid #605AFB !important',
    boxShadow: 'none !important',
  },
  _invalid: {
    outline: '2px solid #A32020 !important',
  },
};

const {
  generateDurationIntervals,
  generateTimeIntervals,
  getIsoString,
  findClosestTime,
  navigateToWeek,
  findClosestDuration,
  normalizeTimeInput,
} = SchDurationTimeFieldsHelpers || {};
const timeIntervals = generateTimeIntervals?.() || [];
const durationIntervals = generateDurationIntervals?.() || [];

const { isConflictingWithBulkBookedEvents } = bulkManualSchedulerUtils;

export const useSchDurationTimeFields = ({
  updateSharedData,
  interviewDataRef,
  registerForUpdates,
  calendarRef,
  formRef,
  variant,
  isBulkManual,
  styleVersion = 'v1',
}) => {
  const containerRef = useRef(null);
  const { bookedSlot } = interviewDataRef.current;
  const isSelfSchedule = variant === 'self';

  // Track the last changed field to avoid circular updates
  const lastChangedFieldRef = useRef(null);

  const { dateFormat } = useDateFormat();

  // Effect to handle updates from calendar to form
  useEffect(() => {
    const unregisterFormFields = registerForUpdates(
      '[bookedSlot, datesSet]',
      (data) => {
        const { fullData } = data;
        const { bookedSlot, datesSet } = fullData;
        const { setValues, values } = formRef.current || {};
        const timezone = datesSet?.timeZone || moment.tz.guess();

        // Ensure we have valid date objects before formatting
        if (!bookedSlot?.start || !bookedSlot?.end) return;

        const start = moment.tz(bookedSlot?.start, timezone);
        const end = moment.tz(bookedSlot?.end, timezone);

        // Additional validation to prevent invalid dates
        if (!start.isValid() || !end.isValid()) {
          return;
        }

        if (!setValues) return;

        // Update form values from calendar
        lastChangedFieldRef.current = 'calendar';
        setValues({
          ...values,
          startDate: start.format(dateFormat?.label),
          endDate: end.format(dateFormat?.label),
          startTime: start.format('hh:mm A'),
          endTime: end.format('hh:mm A'),
          duration: bookedSlot?.duration,
        });
      }
    );
    return () => unregisterFormFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFormat?.label, registerForUpdates]);

  const dateFieldsPopoverRef = {
    type: 'custom',
    Field: () => <Box ref={containerRef}></Box>,
  };

  const durationConfig = {
    name: 'duration',
    label: 'Duration',
    type: 'select',
    isRequired: true,
    options: durationIntervals,
    containerStyles: { width: '100%' },
    customOnChange: (option) => {
      try {
        // Parse the new duration value, default to 30 if invalid, and cap at 24 hours (1440 minutes)
        const newDuration = Math.min(parseInt(option?.value, 10) || 30, 1440);

        const timezone =
          interviewDataRef.current.datesSet?.timeZone || moment.tz.guess();
        const { values: formValues } = formRef.current || {};

        // Extract current values, keeping only what we need for efficiency
        const { startDate, startTime } = formValues;

        // Calculate the new end moment based on start time + new duration
        const startIso = getIsoString(
          startDate,
          startTime,
          timezone,
          dateFormat?.label
        );
        if (!startIso) {
          const updates = {
            'bookedSlot.duration': newDuration,
          };
          updateSharedData(updates);
          return;
        }

        const startMoment = moment.tz(startIso, timezone);
        const newEndMoment = startMoment.clone().add(newDuration, 'minutes');

        // Parse the end string to derive endDate and endTime for form updates
        const calculatedEndDate = newEndMoment.format(dateFormat?.label);
        const calculatedEndTime = newEndMoment.format('hh:mm A');

        const newEnd = getIsoString(
          calculatedEndDate,
          calculatedEndTime,
          timezone,
          dateFormat?.label
        );

        // Prepare updates for booked slot (using end string) and form
        const updates = {
          'bookedSlot.duration': newDuration,
          'bookedSlot.end': newEnd, // Use the ISO string with date, time, and timezone
        };

        // Update form and shared data, preventing circular updates
        if (lastChangedFieldRef.current !== 'duration') {
          updateSharedData(updates);
        }
      } catch (error) {
        console.error('Error updating duration:', error);
      }
    },
  };

  const datesInfo = {
    isConditional: true,
    conditionConfig: { field: 'showDisplayMode', value: 'info' },
    type: 'custom',
    Field: ({ formik }) => {
      const { setFieldValue, values } = formik || {};
      const { startDate, startTime, endDate, endTime } = values;
      const isEndDateSame = startDate === endDate;

      if (!startDate) return null;

      return (
        <Flex
          w="100%"
          justifyContent="space-between"
          bg="secondary-25"
          p="12px 17px"
          gap="12px"
          borderRadius="6px"
        >
          <Flex gap="6px" flexWrap="wrap">
            {isEndDateSame ? (
              <>
                <Flex gap="6px">
                  <Icon
                    // @ts-ignore
                    as={PiCalendarBlankDuotone}
                    w="18px"
                    h="18px"
                  />
                  <KText variant="small_text" color="secondary-700" minW="75px">
                    {startDate}
                  </KText>
                </Flex>
                <Flex gap="6px">
                  <Icon as={Clock} />
                  <KText variant="small_text" color="secondary-700">
                    {startTime} - {endTime}
                  </KText>
                </Flex>
              </>
            ) : (
              <>
                <Flex gap="6px">
                  <Flex gap="6px">
                    <Icon
                      // @ts-ignore
                      as={PiCalendarBlankDuotone}
                      w="18px"
                      h="18px"
                    />
                    <KText
                      variant="small_text"
                      color="secondary-700"
                      minW="75px"
                      data-test-id="meeting-slot-start-date"
                    >
                      {startDate}
                    </KText>
                  </Flex>

                  <Flex gap="6px">
                    <Icon as={Clock} />
                    <KText
                      variant="small_text"
                      color="secondary-700"
                      data-test-id="meeting-slot-start-time"
                    >
                      {startTime}
                    </KText>
                  </Flex>
                </Flex>

                <Flex gap="6px">
                  <Flex gap="6px">
                    <Icon
                      // @ts-ignore
                      as={PiCalendarBlankDuotone}
                      w="18px"
                      h="18px"
                    />
                    <KText
                      variant="small_text"
                      color="secondary-700"
                      minW="75px"
                      data-test-id="meeting-slot-end-date"
                    >
                      {endDate}
                    </KText>
                  </Flex>

                  <Flex gap="6px">
                    <Icon as={Clock} />
                    <KText
                      variant="small_text"
                      color="secondary-700"
                      data-test-id="meeting-slot-end-time"
                    >
                      {endTime}
                    </KText>
                  </Flex>
                </Flex>
              </>
            )}
          </Flex>
          <Icon
            cursor="pointer"
            as={Edit05}
            onClick={() => setFieldValue('showDisplayMode', 'dates')}
            data-test-id="edit-meeting-slot-button"
          />
        </Flex>
      );
    },
  };

  const startDateConfig = {
    isConditional: true,
    conditionConfig: { field: 'showDisplayMode', value: 'dates' },
    name: 'startDate',
    label: 'Start date',
    type: 'filterDatePicker',
    isRequired: true,
    containerStyles: {
      width: '100%',
      flex: 1,
      minWidth: '45%',
    },
    containerRef: containerRef,
    isCustomize: true,
    styleVersion,
    inputStyles: styleVersion === 'v1' ? inputStyles : {},
    isSetFormattedDate: true,
    skipDateSetting: true,
    dayPickerProps: {
      disabled: {
        before: (() => {
          const timezone =
            interviewDataRef.current.datesSet?.timeZone || moment.tz.guess();
          const currentMoment = moment().tz(timezone);
          const today = currentMoment.clone().startOf('day');
          const formValues = formRef.current?.values || {};
          const startTime =
            formValues.startTime || currentMoment.format('hh:mm A');

          // Check if selected time is in the past for today
          const scheduledTime = moment.tz(
            `${today.format('YYYY-MM-DD')} ${startTime}`,
            'YYYY-MM-DD hh:mm A',
            timezone
          );

          if (scheduledTime.isBefore(currentMoment)) {
            return currentMoment.clone().add(1, 'day').startOf('day').toDate();
          }

          return today.toDate();
        })(),
      },
      // Default value when not customizing
    },
    customOnChange: (value) => {
      if (!value) return; // Early return for invalid input

      try {
        const timezone =
          interviewDataRef.current.datesSet?.timeZone || moment.tz.guess();
        const { values: formValues } = formRef.current || {};

        // Extract only what we need to minimize object spread
        const newStartDate = value;
        const {
          startTime,
          endDate: currentEndDate,
          endTime: currentEndTime,
          duration,
        } = formValues;

        // Cap duration at 24 hours (1440 minutes) to prevent overly long events
        const effectiveDuration = Math.min(parseInt(duration, 10) || 30, 1440);

        // Create start moment with the new date and current time
        const startMoment = moment.tz(
          `${newStartDate} ${startTime}`,
          `${dateFormat?.label} hh:mm A`,
          timezone
        );

        if (!startMoment.isValid()) {
          throw new Error('Invalid start date/time combination');
        }

        // Calculate end moment based on duration
        const calculatedEndMoment = startMoment
          .clone()
          .add(effectiveDuration, 'minutes');
        const newEndDate = calculatedEndMoment.format(dateFormat?.label);
        const calculatedEndTime = calculatedEndMoment.format('hh:mm A');

        // Find the closest available end time from timeIntervals
        const newEndTime = findClosestTime(calculatedEndTime, timeIntervals);

        // Determine if we need to update the end date
        const currentStartDate =
          formValues?.startDate ||
          moment.tz(bookedSlot?.start, timezone).format(dateFormat?.label);
        let updatedEndDate = currentEndDate;

        const currentEndDateMoment = moment(currentEndDate, dateFormat?.label);
        const newStartDateMoment = moment(newStartDate, dateFormat?.label);

        if (currentEndDateMoment.isBefore(newStartDateMoment)) {
          // End date is before the new start date, use calculated end date
          updatedEndDate = newEndDate;
        } else if (
          currentEndDate === currentStartDate &&
          currentStartDate !== newStartDate
        ) {
          // End date was equal to the old start date, so update it to the new end date
          updatedEndDate = newEndDate;
        }

        // If end date stayed the same but we need to ensure end time is valid
        // If on the same day, end time must be after start time
        let updatedEndTime = currentEndTime;
        if (updatedEndDate === newStartDate) {
          const startTimeMoment = moment(startTime, 'hh:mm A');
          const endTimeMoment = moment(currentEndTime, 'hh:mm A');

          if (endTimeMoment.isSameOrBefore(startTimeMoment)) {
            // End time is before or equal to start time on the same day
            // Use the calculated end time
            updatedEndTime = newEndTime;
          }
        }

        // Create ISO strings for start and end with proper time zone handling
        const newStart = getIsoString(
          newStartDate,
          startTime,
          timezone,
          dateFormat?.label
        );

        // Important: Create the end time in the same timezone context
        const newEnd = getIsoString(
          updatedEndDate,
          updatedEndTime,
          timezone,
          dateFormat?.label
        );

        updateSharedData({
          'bookedSlot.start': newStart,
          'bookedSlot.end': newEnd,
        });

        navigateToWeek(newStart, timezone, calendarRef);
      } catch (error) {
        console.error('Error updating start date:', error);
      }
    },
  };

  function AddOneDay(date, timezone) {
    if (!moment) {
      console.error('Moment.js is required for timezone handling.');
      return null;
    }

    const momentDate = moment(date, dateFormat?.label).tz(timezone);
    const today = moment().tz(timezone).startOf('day');

    if (momentDate.isSame(today, 'day')) {
      return momentDate.add(1, 'day').toDate();
    } else {
      return date;
    }
  }

  const endDateConfig = {
    isConditional: true,
    conditionConfig: {
      type: 'and',
      conditions: [
        { field: 'showDisplayMode', value: 'dates' },
        {
          field: 'startDate',
          type: 'custom',
          customFunc: (_, allValues) => {
            const isSameDay = allValues?.startDate === allValues?.endDate;
            return !isSameDay && allValues.endDate;
          },
        },
      ],
    },
    // add custom config, based on the start date here
    name: 'endDate',
    label: 'End date',
    type: 'filterDatePicker',
    isRequired: true,
    containerStyles: { width: '48%', flexShrink: 0 },
    containerRef: containerRef,
    styleVersion,
    isCustomize: true,
    inputStyles: styleVersion === 'v1' ? inputStyles : {},
    isSetFormattedDate: true,
    skipDateSetting: true,
    dayPickerProps: {
      disabled: {
        before: (() => {
          const timezone =
            interviewDataRef.current.datesSet?.timeZone || moment.tz.guess();
          const baseDate = new Date(
            bookedSlot?.startDate || moment().tz(timezone).toDate()
          );
          return AddOneDay(baseDate, timezone);
        })(),
      },
    },
    customOnChange: (value) => {
      if (!value) return; // Early return for invalid input

      try {
        const timezone =
          interviewDataRef.current.datesSet?.timeZone || moment.tz.guess();
        const { values: formValues } = formRef.current || {};

        // Extract only necessary fields for efficiency
        const newEndDate = value;
        const {
          startDate: currentStartDate,
          startTime,
          endTime,
          duration,
        } = formValues;

        // Cap duration at 24 hours (1440 minutes)
        const effectiveDuration = Math.min(parseInt(duration, 10) || 30, 1440);

        // Create moment objects for current end and start times/dates
        const endMoment = moment.tz(
          `${newEndDate} ${endTime}`,
          `${dateFormat?.label} hh:mm A`,
          timezone
        );
        const startMoment = moment.tz(
          `${currentStartDate} ${startTime}`,
          `${dateFormat?.label} hh:mm A`,
          timezone
        );
        if (!endMoment.isValid() || !startMoment.isValid())
          throw new Error('Invalid date/time');

        // Calculate theoretical start moment based on new end time and duration
        const calculatedStartMoment = endMoment
          .clone()
          .subtract(effectiveDuration, 'minutes');
        const calculatedStartDate = calculatedStartMoment.format(
          dateFormat?.label
        );
        const isSameDay = endMoment.isSame(calculatedStartMoment, 'day');

        // Get moment objects for date comparison
        const newEndDateMoment = moment(newEndDate, dateFormat?.label);
        const currentStartDateMoment = moment(
          currentStartDate,
          dateFormat?.label
        );

        // Determine if start date needs adjustment
        let newStartDate = currentStartDate;
        const durationInMinutes = endMoment.diff(startMoment, 'minutes');

        // Only update start date if:
        // 1. New end date is before current start date, or
        // 2. Duration exceeds 24 hours (shouldn't happen due to cap, but as a safeguard), or
        // 3. Duration crosses midnight and current start date is incorrect for the new end
        if (
          newEndDateMoment.isBefore(currentStartDateMoment) ||
          durationInMinutes > 1440 ||
          (!isSameDay && currentStartDate === newEndDate)
        ) {
          newStartDate = calculatedStartDate;
        }

        // If no adjustment needed (dates are valid and within constraints), keep start date unchanged
        if (
          newStartDate === currentStartDate &&
          durationInMinutes <= 1440 &&
          newEndDateMoment.isSameOrAfter(currentStartDateMoment)
        ) {
          newStartDate = currentStartDate; // Explicitly keep unchanged
        }

        // Format ISO strings for updates
        const newStart = getIsoString(
          newStartDate,
          startTime,
          timezone,
          dateFormat?.label
        );
        const newEnd = getIsoString(
          newEndDate,
          endTime,
          timezone,
          dateFormat?.label
        );

        // Update only necessary fields, preserving unchanged values
        updateSharedData({
          'bookedSlot.start': newStart,
          'bookedSlot.end': newEnd,
        });

        navigateToWeek(newStart, timezone, calendarRef);
      } catch (error) {
        console.error('Error updating end date:', error);
      }
    },
  };

  const startTimeConfig = {
    isConditional: true,
    conditionConfig: { field: 'showDisplayMode', value: 'dates' },
    name: 'startTime',
    label: 'Start time',
    type: 'selectWithCreate',
    isRequired: true,
    containerStyles: { width: '45%', flex: 1 },
    isAppendToBody: true,
    showErrorInUi: false,
    styleVersion,
    validation: Yup.string()
      .required('Start time is required.')
      .test(
        'bulk-conflict-start-time',
        'Another interview is booked at this slot. Select an available time.',
        function (value) {
          if (!isBulkManual) return true;

          const allFormValues = this.parent;
          const { startDate, duration } = allFormValues;
          const startTime = value;

          if (!startDate || !startTime || !duration || !dateFormat?.label) {
            return true;
          }

          const timezone =
            interviewDataRef.current.datesSet?.timeZone || moment.tz.guess();

          const startMoment = moment.tz(
            `${startDate} ${startTime}`,
            `${dateFormat.label} hh:mm A`,
            timezone
          );

          if (!startMoment.isValid()) {
            return true;
          }

          // Calculate endMoment based on startTime and duration
          const effectiveDuration = Math.min(
            parseInt(duration, 10) || 30,
            1440
          );
          const endMoment = startMoment
            .clone()
            .add(effectiveDuration, 'minutes');

          const prospectiveSlot = {
            start: startMoment.toISOString(),
            end: endMoment.toISOString(),
            timezone: timezone,
          };

          const { bulkBookedEvents } = interviewDataRef.current;
          // Ensure bulkBookedEvents is an array before passing to the utility
          const eventsToCompare = Array.isArray(bulkBookedEvents)
            ? bulkBookedEvents
            : [];

          const isConflicting = isConflictingWithBulkBookedEvents(
            eventsToCompare,
            prospectiveSlot
          );
          return !isConflicting;
        }
      ),
    getCustomOptions: () => {
      const { values } = formRef.current || {};
      const allIntervals = timeIntervals; // Assuming timeIntervals is in scope, e.g., from generateTimeIntervals

      // If no form values or no startDate, return all intervals (minimal functionality)
      if (!values || !values.startDate) {
        return allIntervals;
      }

      const { startDate, endDate } = values;

      const timezone =
        interviewDataRef.current.datesSet?.timeZone || moment.tz.guess();

      // Define today and current time
      const today = moment
        .tz(startDate, dateFormat?.label, false, timezone)
        .startOf('day');
      const now = moment.tz(timezone);

      // Parse startDate and endDate with strict validation
      const startDateMoment = moment.tz(
        startDate,
        dateFormat?.label,
        false,
        timezone
      );
      const endDateMoment = endDate
        ? moment.tz(endDate, dateFormat?.label, false, timezone)
        : null;

      // If startDate is invalid, return all intervals
      if (!startDateMoment.isValid()) {
        return allIntervals;
      }

      // If endDate exists and is invalid, return all intervals
      if (endDate && !endDateMoment?.isValid()) {
        return allIntervals;
      }

      // If startDate and endDate differ (and endDate exists), return all intervals
      if (endDateMoment && !startDateMoment.isSame(endDateMoment, 'day')) {
        return allIntervals;
      }

      // Check if startDate is today
      const isStartDateToday = startDateMoment.isSame(today, 'day');
      if (!isStartDateToday) {
        return allIntervals;
      }

      // Filter options: only times after the current time (now) on today's date
      const filteredOptions = allIntervals.filter((option) => {
        const optionTime = moment
          .tz(option.value, 'hh:mm A', timezone)
          .year(today.year())
          .month(today.month())
          .date(today.date())
          .second(0)
          .millisecond(0);

        if (!optionTime.isValid()) {
          return false;
        }

        const isAfterNow = optionTime.isAfter(now);
        return isAfterNow;
      });

      return filteredOptions; // No fallback to allIntervals unless earlier conditions met
    },
    formatCreateLabel: (userInput) => {
      const formattedTime = normalizeTimeInput(userInput, formRef);
      return <KText>{formattedTime}</KText>;
    },
    customNoOptionsMessage: (props) => {
      const searchTerm = props?.selectProps?.inputValue;
      const { values } = formRef.current || {};
      const { startDate } = values || {};

      const timezone =
        interviewDataRef.current.datesSet?.timeZone || moment.tz.guess();
      const today = moment.tz(startDate, timezone).format(dateFormat?.label);

      if (searchTerm && startDate === today) {
        const normalizedTime = normalizeTimeInput(searchTerm, formRef);
        if (normalizedTime) {
          const inputMoment = moment.tz(normalizedTime, 'h:mm A', timezone);
          const now = moment.tz(timezone);
          if (inputMoment.isValid() && inputMoment.isSameOrBefore(now)) {
            return (
              <components.NoOptionsMessage {...props}>
                <Box fontSize="14px" color="error-500" textAlign="left">
                  {'Start time must be in the future'}
                </Box>
              </components.NoOptionsMessage>
            );
          }
        }
      }

      return (
        <components.NoOptionsMessage {...props}>
          <Box fontSize="14px" color="secondary-700" textAlign="left">
            {'Please enter a valid time (e.g., 11:30 AM)'}
          </Box>
        </components.NoOptionsMessage>
      );
    },
    validateNewOption: (userEnteredTime) => {
      if (!userEnteredTime) return false;

      try {
        const formattedTime = normalizeTimeInput(userEnteredTime, formRef);
        if (!formattedTime) return false;

        const { values } = formRef.current || {};
        const { startDate } = values || {};
        const timezone =
          interviewDataRef.current.datesSet?.timeZone || moment.tz.guess();
        const today = moment.tz(startDate, timezone).format(dateFormat?.label);

        if (startDate === today) {
          const inputMoment = moment.tz(formattedTime, 'h:mm A', timezone);
          const now = moment.tz(timezone);
          if (inputMoment.isValid() && inputMoment.isSameOrBefore(now)) {
            return false; // Reject past times on current date
          }
        }

        formattedTimeRef.current = formattedTime;
        return true;
      } catch (error) {
        console.error('Error validating start time input:', error);
        return false;
      }
    },
    customOnChange: (option) => {
      try {
        if (!option?.value) return;

        const timezone =
          interviewDataRef.current.datesSet?.timeZone || moment.tz.guess();
        const { values: formValues } = formRef.current || {};

        const startTime = option.value;
        const { startDate, duration } = formValues;
        const effectiveDuration = Math.min(parseInt(duration, 10) || 30, 1440);

        // Create start moment with NEW start time
        const startMoment = moment.tz(
          `${startDate} ${startTime}`,
          `${dateFormat?.label} hh:mm A`,
          timezone
        );
        if (!startMoment.isValid()) return;

        // ALWAYS calculate the end moment based on the new start time and duration
        const newEndMoment = startMoment
          .clone()
          .add(effectiveDuration, 'minutes');
        const calculatedEndDate = newEndMoment.format(dateFormat?.label);
        const calculatedEndTime = newEndMoment.format('hh:mm A');

        // Get ISO strings
        const newStart = getIsoString(
          startDate,
          startTime,
          timezone,
          dateFormat?.label
        );
        const newEnd = getIsoString(
          calculatedEndDate,
          calculatedEndTime, // Use the newly calculated end time/date
          timezone,
          dateFormat?.label
        );

        if (!newStart || !newEnd) return;

        const updates = {
          'bookedSlot.start': newStart,
          'bookedSlot.end': newEnd,
          // DO NOT update duration here, it's the basis for calculation
        };

        updateSharedData(updates);
      } catch (error) {
        console.error('Error updating start time', error);
      }
    },
  };

  const endTimeCustomOnChange = (option) => {
    try {
      if (!option?.value) return;

      const timezone =
        interviewDataRef.current.datesSet?.timeZone || moment.tz.guess();
      const { values: formValues } = formRef.current || {};

      // Extract necessary values
      const endTime = option.value;
      const endDate = formValues.endDate;
      const startDate = formValues.startDate;
      const startTime = formValues.startTime;

      // Create new start and end datetime
      const newStart = getIsoString(
        startDate,
        startTime,
        timezone,
        dateFormat?.label
      );
      const newEnd = getIsoString(
        endDate,
        endTime,
        timezone,
        dateFormat?.label
      );

      if (!newStart || !newEnd) {
        return;
      }

      // Calculate duration between start and end
      const durationMinutes = moment
        .tz(newEnd, timezone)
        .diff(moment.tz(newStart, timezone), 'minutes');

      const closestDuration = findClosestDuration(
        durationMinutes,
        durationIntervals
      );

      // Calculate start time by subtracting the exact duration
      const adjustedStartMoment = moment
        .tz(newEnd, timezone)
        .subtract(closestDuration, 'minutes');

      const adjustedStart = adjustedStartMoment.toISOString();

      // Update booked slot
      const updates = {
        'bookedSlot.start': adjustedStart,
        'bookedSlot.end': newEnd,
        'bookedSlot.duration': closestDuration,
      };

      // Prevent circular updates
      if (lastChangedFieldRef.current !== 'endTime') {
        lastChangedFieldRef.current = 'endTime';
        updateSharedData(updates);
      }
    } catch (error) {
      console.error('Error updating end time', error);
    }
  };

  const endTimeConfig = {
    name: 'endTime',
    label: 'End time',
    type: 'selectWithCreate',
    styleVersion,
    isRequired: true,
    containerStyles: { width: '48%' },
    isConditional: true,
    showErrorInUi: false,
    conditionConfig: { field: 'showDisplayMode', value: 'dates' },
    validation: Yup.string()
      .required('End time is required.')
      .test(
        'bulk-conflict-end-time',
        'Another interview is booked at this slot. Select an available time.',
        function (value) {
          if (!isBulkManual) return true;

          const allFormValues = this.parent;
          const { startDate, startTime, endDate } = allFormValues;
          const endTime = value;
          const effectiveEndDate = endDate || startDate;

          if (
            !startDate ||
            !startTime ||
            !effectiveEndDate ||
            !endTime ||
            !dateFormat?.label
          ) {
            return true;
          }

          const timezone =
            interviewDataRef.current.datesSet?.timeZone || moment.tz.guess();

          const startMoment = moment.tz(
            `${startDate} ${startTime}`,
            `${dateFormat.label} hh:mm A`,
            timezone
          );
          const endMoment = moment.tz(
            `${effectiveEndDate} ${endTime}`,
            `${dateFormat.label} hh:mm A`,
            timezone
          );

          if (!startMoment.isValid() || !endMoment.isValid()) {
            return true;
          }

          const prospectiveSlot = {
            start: startMoment.toISOString(),
            end: endMoment.toISOString(),
            timezone: timezone,
          };

          const { bulkBookedEvents } = interviewDataRef.current;

          const isConflicting = isConflictingWithBulkBookedEvents(
            bulkBookedEvents,
            prospectiveSlot
          );
          return !isConflicting;
        }
      ),
    getCustomOptions: () => {
      const { values } = formRef.current || {};
      const allIntervals = timeIntervals; // Assuming timeIntervals is in scope

      // If no form values, return all intervals
      if (!values) {
        return allIntervals;
      }

      const { startTime, startDate, endDate } = values;

      const timezone =
        interviewDataRef.current.datesSet?.timeZone || moment.tz.guess();

      // Validate startDate and endDate (assuming ISO-like "YYYY-MM-DD" format from your helpers)
      const today = moment
        .tz(startDate, dateFormat?.label, false, timezone)
        .startOf('day'); // e.g., "2025-03-10 00:00:00"
      const startDateMoment = startDate
        ? moment.tz(startDate, dateFormat?.label, false, timezone)
        : null;
      const endDateMoment = endDate
        ? moment.tz(endDate, dateFormat?.label, false, timezone)
        : null;

      // If dates are invalid or missing, return all intervals
      if (startDate && !startDateMoment?.isValid()) {
        console.warn('Invalid startDate:', startDate);
        return allIntervals;
      }
      if (endDate && !endDateMoment?.isValid()) {
        console.warn('Invalid endDate:', endDate);
        return allIntervals;
      }

      // If startDate and endDate differ, return all intervals
      if (
        startDateMoment &&
        endDateMoment &&
        !startDateMoment.isSame(endDateMoment, 'day')
      ) {
        return allIntervals;
      }

      // Only filter if startDate is today
      const isStartDateToday =
        startDateMoment && startDateMoment.isSame(today, 'day');
      if (!isStartDateToday || !startTime) {
        return allIntervals;
      }

      // Normalize and validate startTime
      const normalizedStartTime = normalizeTimeInput(startTime, formRef);
      const startMoment = normalizedStartTime
        ? moment.tz(normalizedStartTime, 'hh:mm A', timezone)
        : null;

      if (!startMoment || !startMoment.isValid()) {
        console.warn('Invalid startTime:', startTime);
        return allIntervals;
      }

      // Filter options: only times after startTime on today's date
      const filteredOptions = allIntervals.filter((option) => {
        const optionTime = moment
          .tz(option.value, 'hh:mm A', timezone)
          .year(today.year())
          .month(today.month())
          .date(today.date())
          .second(0)
          .millisecond(0);

        if (!optionTime.isValid()) {
          console.warn('Invalid option time:', option.value);
          return false; // Skip invalid options
        }

        // Set startMoment to today's date for consistent comparison
        const startMomentToday = startMoment
          .clone()
          .year(today.year())
          .month(today.month())
          .date(today.date())
          .second(0)
          .millisecond(0);

        return optionTime.isAfter(startMomentToday);
      });

      // Return filtered options if any, otherwise all intervals
      return filteredOptions.length > 0 ? filteredOptions : allIntervals;
    },
    formatCreateLabel: (userInput) => {
      const formattedTime = normalizeTimeInput(userInput, formRef);
      return <KText>{formattedTime}</KText>;
    },
    createPlaceHolder: 'add time',
    customNoOptionsMessage: (props) => {
      const searchTerm = props?.selectProps?.inputValue;
      const { values } = formRef.current || {};
      const { startTime } = values || {};

      const timezone =
        interviewDataRef.current.datesSet?.timeZone || moment.tz.guess();

      if (searchTerm && startTime) {
        const normalizedTime = normalizeTimeInput(searchTerm, formRef);
        if (normalizedTime) {
          const inputMoment = moment.tz(normalizedTime, 'h:mm A', timezone);
          const startMoment = moment.tz(startTime, 'hh:mm A', timezone);

          if (
            inputMoment.isValid() &&
            startMoment.isValid() &&
            inputMoment.isSameOrBefore(startMoment)
          ) {
            return (
              <components.NoOptionsMessage {...props}>
                <Box
                  fontSize="14px"
                  borderColor="border-color"
                  color="error-500"
                  textAlign="left"
                >
                  {'End time must be after start time'}
                </Box>
              </components.NoOptionsMessage>
            );
          }
        }

        return (
          <components.NoOptionsMessage {...props}>
            <Box
              fontSize="14px"
              borderColor="border-color"
              color="secondary-700"
              textAlign="left"
            >
              {'Please enter a valid time (e.g., 11:30 AM)'}
            </Box>
          </components.NoOptionsMessage>
        );
      }

      return (
        <components.NoOptionsMessage {...props}>
          <Box
            fontSize="14px"
            borderColor="border-color"
            color="secondary-700"
            textAlign="left"
          >
            {'Enter a time to add as a new option'}
          </Box>
        </components.NoOptionsMessage>
      );
    },
    validateNewOption: (userEnteredTime) => {
      if (!userEnteredTime) {
        return false;
      }

      try {
        // Normalize and format the time
        const formattedTime = normalizeTimeInput(userEnteredTime, formRef);
        if (!formattedTime) {
          return false;
        }

        // Validate against start time
        const { values } = formRef.current || {};
        const { startTime, startDate, endDate } = values || {};

        const timezone =
          interviewDataRef.current.datesSet?.timeZone || moment.tz.guess();

        if (startTime && startDate === endDate) {
          const inputMoment = moment.tz(formattedTime, 'h:mm A', timezone);
          const startMoment = moment.tz(startTime, 'hh:mm A', timezone);

          if (
            inputMoment.isValid() &&
            startMoment.isValid() &&
            inputMoment.isSameOrBefore(startMoment)
          ) {
            return false;
          }
        }

        // Store the formatted time for customOnChange
        formattedTimeRef.current = formattedTime;
        return formattedTime ? true : false;
      } catch (error) {
        console.error('Error validating time input:', error);
        return false;
      }
    },
    isAppendToBody: true,
    customOnChange: endTimeCustomOnChange,
  };

  const bulkConflictError = {
    type: 'custom',
    Field: (props) => (
      <SchBulkConflictError
        formRef={formRef}
        isBulkManual={isBulkManual}
        interviewDataRef={interviewDataRef}
        registerForUpdates={registerForUpdates}
        {...props}
      />
    ),
  };

  // Add this ref to store formatted time between validation and onChange
  const formattedTimeRef = React.useRef(null);

  if (isSelfSchedule) return [durationConfig];

  // for manual return all these fields
  const fields = [
    dateFieldsPopoverRef,
    durationConfig,
    datesInfo,
    {
      type: 'wrapper',
      Wrapper: Flex,
      wrapperProps: {
        gap: '12px',
      },
      fields: [startDateConfig, endDateConfig],
    },
    {
      type: 'wrapper',
      Wrapper: Flex,
      wrapperProps: { gap: '12px' },
      fields: [startTimeConfig, endTimeConfig],
    },
    bulkConflictError,
  ];

  return fields;
};
